import styled from "styled-components";
import { Button, Typography, Empty } from "antd";

const STATUS_COLORS = {
  sent: { background: "#fef3c7", color: "#92400e", border: "#fde68a" },
  bidding: { background: "#cffafe", color: "#155e75", border: "#a5f3fc" },
  awarded: { background: "#f3e8ff", color: "#6b21a8", border: "#e9d5ff" },
  accepted: { background: "#dbeafe", color: "#1e40af", border: "#bfdbfe" },
  pending_acceptance: { background: "#d1fae5", color: "#065f46", border: "#a7f3d0" },
  cancelled: { background: "#fee2e2", color: "#991b1b", border: "#fecaca" },
  closed: { background: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" },
  not_awarded: { background: "#fee2e2", color: "#991b1b", border: "#fecaca" },
};

const statusColor = (status, property) =>
  STATUS_COLORS[status]?.[property] || STATUS_COLORS.closed[property];

export const Page = styled.div`
  width: 100%;
  min-width: 0;
  padding: 16px;
  color: #1f2937;

  @media (max-width: 767px) {
    padding: 8px;
  }
`;

export const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const HeaderIdentity = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 12px;

  @media (max-width: 767px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;

export const HeaderCopy = styled.div`
  min-width: 0;
`;

export const HeaderTitle = styled(Typography.Title)`
  margin: 0 !important;
  font-size: 20px !important;
  line-height: 1.35 !important;
`;

export const RequestId = styled(Typography.Text)`
  display: block;
  max-width: min(58vw, 640px);
  overflow: hidden;
  color: #6b7280;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 767px) {
    max-width: calc(100vw - 72px);
  }
`;

export const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

export const BackButton = styled(Button)`
  flex: 0 0 auto;
`;

export const ActionBar = styled.div`
  display: flex;
  align-items: center;
  min-height: 54px;
  padding: 10px 14px;
  margin-bottom: 18px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;

  ${ActionGroup} {
    width: 100%;
    justify-content: flex-start;
  }

  @media (max-width: 767px) {
    ${ActionGroup} {
      justify-content: stretch;
    }

    .ant-btn {
      flex: 1 1 auto;
    }
  }
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 3px 10px;
  border: 1px solid ${({ $status }) => statusColor($status, "border")};
  border-radius: 999px;
  background: ${({ $status }) => statusColor($status, "background")};
  color: ${({ $status }) => statusColor($status, "color")};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

export const Stepper = styled.div`
  width: 100%;
  min-width: 0;
  padding: 24px 10px 18px;
  margin-bottom: 18px;
  overflow-x: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

export const StepTrack = styled.div`
  display: flex;
  align-items: flex-start;
  min-width: 480px;
`;

export const StepItem = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 0;
  min-width: 92px;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export const StepDot = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 2px solid ${({ $active, $done }) =>
    $active ? "#1677ff" : $done ? "#16a34a" : "#d1d5db"};
  border-radius: 50%;
  background: ${({ $active, $done }) =>
    $active ? "#1677ff" : $done ? "#16a34a" : "#f3f4f6"};
  color: ${({ $active, $done }) => ($active || $done ? "#fff" : "#9ca3af")};
  font-size: 15px;
  box-shadow: ${({ $active }) => ($active ? "0 0 0 4px rgba(22, 119, 255, .16)" : "none")};
`;

export const StepLabel = styled.span`
  max-width: 104px;
  margin-top: 8px;
  color: ${({ $active, $done }) => ($active ? "#1677ff" : $done ? "#16a34a" : "#9ca3af")};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
`;

export const StepConnector = styled.div`
  position: absolute;
  z-index: 0;
  top: 17px;
  right: -50%;
  left: 50%;
  height: 2px;
  background: ${({ $done, $active }) => ($done ? "#16a34a" : $active ? "#bfdbfe" : "#e5e7eb")};
`;

export const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $spread }) => ($spread ? "space-between" : "flex-start")};
  gap: 8px;
  color: #111827;
  font-size: 15px;
  font-weight: 700;
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const DetailCard = styled.section`
  min-width: 0;
  padding: 20px;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;

  ${SectionHeading} {
    margin-bottom: 16px;
  }
`;

export const DetailFieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const DetailField = styled.div`
  min-width: 0;
  margin-bottom: 12px;
`;

export const DetailLabel = styled.div`
  margin-bottom: 5px;
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
`;

export const DetailValue = styled.div`
  min-height: 36px;
  padding: 8px 11px;
  overflow-wrap: anywhere;
  border: 1px solid #f3f4f6;
  border-radius: 6px;
  background: #f9fafb;
  color: ${({ $priority }) =>
    $priority === "Urgent" ? "#dc2626" : $priority === "Important" ? "#b45309" : "#1f2937"};
  font-size: 14px;
  font-weight: ${({ $priority }) => ($priority ? 600 : 400)};
`;

export const DescriptionValue = styled.div`
  min-height: 86px;
  padding: 12px;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  border: 1px solid #f3f4f6;
  border-radius: 6px;
  background: #f9fafb;
  color: #374151;
  font-size: 14px;
  line-height: 1.6;
`;

export const Timeline = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TimelineItem = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

export const TimelineMeta = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

export const TimelineAuthor = styled.span`
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
`;

export const TimelineTime = styled.span`
  color: #9ca3af;
  font-size: 12px;
`;

export const TimelineText = styled.div`
  color: #374151;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const EmptyNotes = styled(Empty)`
  margin: 24px 0 10px;
`;

// Attachment tab: one row per file - name (preview / download) on the left,
// date + delete on the right. Deliberately not the notes Timeline: that grid
// reserves a 36px avatar column which has no counterpart here.
export const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AttachmentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

export const AttachmentMain = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  /* AttachmentViewer renders its two actions inside an antd Space. Let the
     container shrink so a long file name ellipsises instead of pushing the
     date / delete buttons out of the row. */
  .ant-space {
    flex: 1;
    min-width: 0;
    max-width: 100%;
  }

  .ant-space-item {
    min-width: 0;
  }

  /* The preview button is the shrinkable one; the download button keeps its
     intrinsic size. */
  .ant-space-item:first-child {
    flex: 1 1 auto;
  }

  .ant-btn {
    display: inline-flex;
    align-items: center;
    min-width: 0;
    max-width: 100%;
  }

  /* Only the label span is blocked out - an antd Button also renders its icon
     as a direct <span class="anticon"> child (antd 5 nests it in
     .ant-btn-icon), and blocking that one stacks the icon above the file
     name instead of keeping both on a single line. */
  .ant-btn > span:not(.anticon):not(.ant-btn-icon) {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const AttachmentSide = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
`;

export const AttachmentDate = styled.span`
  color: #9ca3af;
  font-size: 12px;
  white-space: nowrap;
`;

export const AmountValue = styled.div`
  color: #1f2937;
  font-size: 20px;
  font-weight: 700;
`;

export const ContractorQuoteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ContractorQuoteCard = styled.div`
  min-width: 0;
  padding: 16px;
  border: 1px solid ${({ $awarded }) => ($awarded ? "#86efac" : "#e5e7eb")};
  border-radius: 10px;
  background: ${({ $awarded }) => ($awarded ? "#f0fdf4" : "#fff")};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

export const ContractorQuoteHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

export const ContractorIdentity = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
`;

export const QuoteMeta = styled.div`
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;
`;

export const LatestQuote = styled.div`
  padding: 14px;
  border-radius: 8px;
  background: ${({ $awarded }) => ($awarded ? "#dcfce7" : "#f8fafc")};
`;

export const QuoteHistory = styled.details`
  margin-top: 12px;

  > summary {
    width: fit-content;
    cursor: pointer;
    color: #2563eb;
    font-size: 13px;
    font-weight: 600;
    user-select: none;
  }
`;

export const QuoteRevision = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

export const DetailLink = styled(Button)`
  padding: 0;
  height: auto;
  color: #1677ff !important;
  text-decoration: underline;
  text-align: left;
  white-space: normal;
  line-height: 1.5;
  overflow-wrap: anywhere;

  &:hover,
  &:focus {
    color: #0958d9 !important;
    text-decoration: underline;
  }
`;

export const WorkOrderLink = DetailLink;
export const TenantRequestLink = DetailLink;
